import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../../styles/login.css';

export default function LoginPage() {
  const { login, loading, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Field validation states
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setLocalError('');

    if (!username.trim()) {
      return;
    }
    if (!password) {
      return;
    }

    const success = await login(username.trim(), password);
    if (success) {
      window.location.href = '/';
    }
  };

  const usernameError = submitted && !username.trim();
  const passwordError = submitted && !password;

  return (
    <div className="login-layout">
      {/* Left Branding Panel */}
      <div className="login-branding">
        {/* Logo */}
        <div className="login-logo">
          <span className="logo-icon">◉</span>
          <span className="logo-text">Journey Companion</span>
        </div>

        {/* Hero Content */}
        <div className="login-hero">
          <h2>Manage the Sacred Journey</h2>
          <p>
            Monitor pilgrims, facilities, routes, volunteers, and emergency services
            from a single unified control center.
          </p>

          {/* Feature Highlights Grid */}
          <div className="feature-highlights">
            <div className="feature-card">
              <span className="feature-check">✓</span>
              <div className="feature-info">
                <h4>Live Tracking</h4>
                <p>Real-time location, group status, and route checkpoints.</p>
              </div>
            </div>

            <div className="feature-card">
              <span className="feature-check">✓</span>
              <div className="feature-info">
                <h4>Facility Management</h4>
                <p>Coordinate medical camps, food services, and accommodations.</p>
              </div>
            </div>

            <div className="feature-card">
              <span className="feature-check">✓</span>
              <div className="feature-info">
                <h4>Emergency Response</h4>
                <p>Real-time SOS board with instant dispatching tools.</p>
              </div>
            </div>

            <div className="feature-card">
              <span className="feature-check">✓</span>
              <div className="feature-info">
                <h4>Analytics Dashboard</h4>
                <p>Granular logs on pilgrim metrics and system scalability.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Temple Silhouette SVG Background */}
        <svg 
          className="temple-silhouette" 
          viewBox="0 0 800 300" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          {/* Main temple structure base */}
          <path d="M 0,300 L 800,300 L 800,280 L 760,260 L 730,260 L 710,210 L 680,210 L 670,170 L 640,170 L 620,80 L 600,80 L 590,0 L 585,0 L 575,80 L 555,80 L 535,170 L 505,170 L 495,210 L 465,210 L 445,260 L 415,260 L 375,280 Z" fill="#FFFFFF" />
          {/* Decorative domes */}
          <path d="M 200,300 L 260,300 L 260,240 Q 260,200 230,200 Q 200,200 200,240 Z" fill="#FFFFFF" />
          <path d="M 120,300 L 190,300 L 190,220 Q 190,170 155,170 Q 120,170 120,220 Z" fill="#FFFFFF" />
          <path d="M 50,300 L 110,300 L 110,250 Q 110,210 80,210 Q 50,210 50,250 Z" fill="#FFFFFF" />
          {/* Ground pathway outline */}
          <path d="M 0,300 Q 200,280 400,290 T 800,285 L 800,300 Z" fill="#FFF2E8" />
        </svg>
      </div>

      {/* Right Login Card Panel */}
      <div className="login-card-container">
        <div className="login-card">
          <div className="login-card-header">
            <h3>Admin Portal</h3>
            <p>Please enter your credentials to log in.</p>
          </div>

          {/* API Error Alert Banner */}
          {(error || localError) && (
            <div className="alert-banner" role="alert">
              <span>⚠️</span>
              <div>{localError || error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="form-input-container">
            {/* Username Field */}
            <div className="form-field">
              <label htmlFor="username">Username or Email</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoFocus
                disabled={loading}
                aria-invalid={usernameError ? "true" : "false"}
                aria-describedby={usernameError ? "username-error" : undefined}
                style={{
                  borderColor: usernameError ? '#EF4444' : undefined,
                  boxShadow: usernameError ? '0 0 0 4px rgba(239, 68, 68, 0.15)' : undefined
                }}
              />
              {usernameError && (
                <span className="inline-error" id="username-error">
                  Username is required
                </span>
              )}
            </div>

            {/* Password Field */}
            <div className="form-field">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                  aria-invalid={passwordError ? "true" : "false"}
                  aria-describedby={passwordError ? "password-error" : undefined}
                  style={{
                    borderColor: passwordError ? '#EF4444' : undefined,
                    boxShadow: passwordError ? '0 0 0 4px rgba(239, 68, 68, 0.15)' : undefined
                  }}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && (
                <span className="inline-error" id="password-error">
                  Password is required
                </span>
              )}
            </div>

            {/* Checkbox and Forgot Password Link */}
            <div className="form-meta">
              <label className="checkbox-control">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                Remember me
              </label>
              <a 
                href="#forgot-password" 
                className="forgot-password-link"
                onClick={(e) => {
                  e.preventDefault();
                  alert("Please contact the primary system administrator to reset your credentials.");
                }}
              >
                Forgot Password?
              </a>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn-submit" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-card-footer">
            <p>© 2026 Journey Companion</p>
            <p>Bolbam Yatra Management Platform</p>
            <p className="version">Version 1.0.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}